import { ChevronLeft, ChevronRight, MoreHorizontal } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  totalItems?: number;
  limit?: number;
  onPageChange: (page: number) => void;
  className?: string;
}

function Pagination({
  currentPage,
  totalPages,
  totalItems,
  limit,
  onPageChange,
  className,
}: PaginationProps) {
  // Guard against invalid values
  const safeTotalPages = Number.isFinite(totalPages) && totalPages > 0 ? totalPages : 1;
  const safeCurrentPage = Number.isFinite(currentPage) && currentPage > 0 ? currentPage : 1;

  const getPageNumbers = () => {
    const pages: (number | 'ellipsis')[] = [];
    const maxVisible = 5;

    if (safeTotalPages <= maxVisible) {
      for (let i = 1; i <= safeTotalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);

      if (safeCurrentPage > 3) {
        pages.push('ellipsis');
      }

      const start = Math.max(2, safeCurrentPage - 1);
      const end = Math.min(safeTotalPages - 1, safeCurrentPage + 1);

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      if (safeCurrentPage < safeTotalPages - 2) {
        pages.push('ellipsis');
      }

      pages.push(safeTotalPages);
    }

    return pages;
  };

  // Don't show page numbers if only 1 page
  const pages = safeTotalPages > 1 ? getPageNumbers() : [];

  return (
    <nav
      className={cn(
        'flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6',
        className,
      )}
      aria-label="Pagination"
    >
      <div className="hidden sm:block">
        <p className="text-sm text-gray-700">
          {totalItems !== undefined && limit !== undefined ? (
            <>
              Mostrando{' '}
              <span className="font-medium">
                {Math.min((safeCurrentPage - 1) * limit + 1, totalItems)}
              </span>{' '}
              a <span className="font-medium">{Math.min(safeCurrentPage * limit, totalItems)}</span>{' '}
              de <span className="font-medium">{totalItems}</span> resultados
            </>
          ) : (
            <>
              Página <span className="font-medium">{safeCurrentPage}</span> de{' '}
              <span className="font-medium">{safeTotalPages}</span>
            </>
          )}
        </p>
      </div>
      <div className="flex flex-1 justify-between sm:justify-end gap-1">
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(safeCurrentPage - 1)}
          disabled={safeCurrentPage <= 1}
        >
          <ChevronLeft className="h-4 w-4" />
          <span className="sr-only">Anterior</span>
        </Button>

        {pages.length > 0 && (
          <div className="hidden sm:flex gap-1">
            {pages.map((page, index) => {
              if (page === 'ellipsis') {
                // Create unique key based on position in array
                const ellipsisKey = `ellipsis-${pages.slice(0, index).filter((p) => p === 'ellipsis').length}`;
                return (
                  <span key={ellipsisKey} className="flex items-center px-2">
                    <MoreHorizontal className="h-4 w-4 text-gray-400" />
                  </span>
                );
              }
              return (
                <Button
                  key={page}
                  variant="outline"
                  size="sm"
                  onClick={() => onPageChange(page)}
                  className={
                    safeCurrentPage === page
                      ? 'bg-black text-white hover:bg-gray-800 hover:text-white'
                      : ''
                  }
                >
                  {page}
                </Button>
              );
            })}
          </div>
        )}

        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(safeCurrentPage + 1)}
          disabled={safeCurrentPage >= safeTotalPages}
        >
          <span className="sr-only">Próximo</span>
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </nav>
  );
}

export { Pagination };
